import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Swal from 'sweetalert2';
import '../styles/EnviosEscaneados.css';

/* ================== Utilidades de fecha (semana domingo-sábado) ================== */
// Retorna una copia de la fecha truncada a medianoche local.
const atStartOfDay = (d) => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

// Inicio de semana (domingo 00:00) para una fecha dada (zona local)
const startOfWeekSunday = (d) => {
  const dt = atStartOfDay(d);
  const day = dt.getDay(); // 0=Dom ... 6=Sab
  const start = new Date(dt);
  start.setDate(dt.getDate() - day);
  return start;
};

// Fin de semana (sábado 23:59:59.999) para una fecha dada (zona local)
const endOfWeekSaturday = (d) => {
  const start = startOfWeekSunday(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

// Suma días sin cambiar zona horaria
const addDays = (d, days) => {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
};

// ===== Reemplaza la función de numeración semanal por esta =====
const getSundayWeekNumber52 = (date) => {
  const MSWEEK = 7 * 24 * 60 * 60 * 1000;

  const atStartOfDay = (d) => {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };
  const startOfWeekSunday = (d) => {
    const dt = atStartOfDay(d);
    const day = dt.getDay(); // 0=Dom ... 6=Sab
    const start = new Date(dt);
    start.setDate(dt.getDate() - day);
    return start;
  };
  const endOfWeekSaturday = (d) => {
    const start = startOfWeekSunday(d);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };
  const contains = (start, end, d) => start <= d && d <= end;

  const weekStart = startOfWeekSunday(date);
  const weekEnd = endOfWeekSaturday(date);
  const y = date.getFullYear();

  const jan1 = new Date(y, 0, 1);
  const jan1Prev = new Date(y - 1, 0, 1);
  const jan1Next = new Date(y + 1, 0, 1);

  // Si esta semana contiene el 1 de enero del año y → Semana 1 de y
  if (contains(weekStart, weekEnd, jan1)) {
    return { year: y, weekNumber: 1 };
  }

  // Si esta semana contiene el 1 de enero del año y+1 → pertenece al año y+1
  if (contains(weekStart, weekEnd, jan1Next)) {
    const week1StartNext = startOfWeekSunday(jan1Next);
    let w = 1 + Math.floor((weekStart - week1StartNext) / MSWEEK);
    if (w > 52) w = 52; // forzar máximo 52 semanas
    if (w < 1) w = 1;
    return { year: y + 1, weekNumber: w };
  }

  // Si la semana termina antes del 1 de enero de y → pertenece al año y-1
  if (weekEnd < jan1) {
    const week1StartPrev = startOfWeekSunday(jan1Prev);
    let w = 1 + Math.floor((weekStart - week1StartPrev) / MSWEEK);
    if (w > 52) w = 52;
    if (w < 1) w = 1;
    return { year: y - 1, weekNumber: w };
  }

  // Caso normal: semanas posteriores a la semana 1 dentro del mismo año y
  const week1Start = startOfWeekSunday(jan1);
  let w = 1 + Math.floor((weekStart - week1Start) / MSWEEK);
  if (w > 52) w = 52;
  if (w < 1) w = 1;
  return { year: y, weekNumber: w };
};

const weekStartFromYearWeek52 = (year, week) => {
  const w = Math.min(Math.max(parseInt(week || 1, 10), 1), 52);
  const week1Start = startOfWeekSunday(new Date(year, 0, 1));
  return addDays(week1Start, (w - 1) * 7);
};


const formatRange = (start, end) =>
  `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;

/* ================== Componente ================== */
const EnviosEscaneados = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId } = location.state || {};

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // ✅ Entradas del usuario (semana/año)
  const [{ inputWeek, inputYear }, setWeekInputs] = useState(() => {
    const { weekNumber: w, year: y } = getSundayWeekNumber52(startOfWeekSunday(new Date()));
    return { inputWeek: w, inputYear: y };
  });


  // Filtro semanal
  const [useWeekFilter, setUseWeekFilter] = useState(true);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
    startOfWeekSunday(new Date())
  );

  const selectedWeekEnd = useMemo(
    () => endOfWeekSaturday(selectedWeekStart),
    [selectedWeekStart]
  );

  const { weekNumber, year } = useMemo(
    () => getSundayWeekNumber52(selectedWeekStart),
    [selectedWeekStart]
  );

  useEffect(() => {
    if (companyId) fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ✅ Mantener inputs sincronizados con la semana actual seleccionada
  useEffect(() => {
    const { weekNumber: w, year: y } = getSundayWeekNumber52(selectedWeekStart);
    setWeekInputs({ inputWeek: w, inputYear: y });
  }, [selectedWeekStart]);


  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://api.maderasdelatlantico.com/api/Upload/getfiles?companyId=${companyId}`
      );
      const data = await response.json();
      setFiles(data || []);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return Swal.fire('Error', 'Selecciona un archivo.', 'warning');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('CompanyId', companyId);

    try {
      const response = await fetch('https://api.maderasdelatlantico.com/api/Upload/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Error al subir el archivo');

      const updatedFiles = await response.json();
      setFiles(updatedFiles);
      setShowModal(false);
      setSelectedFile(null);
      Swal.fire('Éxito', 'Archivo subido correctamente.', 'success');
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  // ✅ Aplicar salto a la semana ingresada
  const applyWeekJump = () => {
    const y = parseInt(inputYear, 10);
    if (isNaN(y) || y < 1) {
      Swal.fire('Año inválido', 'Ingresa un año válido (e.g., 2025).', 'warning');
      return;
    }
    const start = weekStartFromYearWeek52(y, inputWeek);
    setSelectedWeekStart(start);
  };

  // ✅ Permitir Enter en los inputs
  const handleWeekKey = (e) => {
    if (e.key === 'Enter') applyWeekJump();
  };


  const handleDeleteFile = async (fileId) => {
    const confirm = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción desactivará el archivo.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, borrar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(
        `https://api.maderasdelatlantico.com/api/Upload/deletefile?FileId=${fileId}&CompanyId=${companyId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) throw new Error('Error al borrar el archivo');

      const updatedFiles = await response.json();
      setFiles(updatedFiles);

      Swal.fire('Eliminado', 'El archivo ha sido eliminado.', 'success');
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  const handleViewFile = async (s3Name) => {
    try {
      const response = await fetch(`https://api.maderasdelatlantico.com/api/Upload/url/${s3Name}`);
      if (!response.ok) throw new Error('Error al obtener URL del archivo');

      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    }
  };

  const formatSize = (bytes) => `${(bytes / 1024).toFixed(2)} KB`;

  // === Filtro combinado: nombre + (opcional) semana ===
  const filteredFiles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = files.filter((file) =>
      file.originalName?.toLowerCase().includes(term)
    );

    if (!useWeekFilter) return list;

    // Mantener solo archivos con creationDate dentro del rango seleccionado (domingo-sábado)
    return list.filter((file) => {
      const d = new Date(file.creationDate);
      // Normalizamos al día local
      const dn = atStartOfDay(d);
      return dn >= atStartOfDay(selectedWeekStart) && dn <= selectedWeekEnd;
    });
  }, [files, searchTerm, useWeekFilter, selectedWeekStart, selectedWeekEnd]);

  // === Controles de semana ===
  const goPrevWeek = () => setSelectedWeekStart((d) => addDays(d, -7));
  const goNextWeek = () => setSelectedWeekStart((d) => addDays(d, +7));
  const goThisWeek = () => setSelectedWeekStart(startOfWeekSunday(new Date()));

  return (
    <div>
      <Header />

      <div className="scanned-files-header">
        <button className="shipment-back-button" onClick={() => navigate(-1)}>
          ← Regresar
        </button>

        <h2 className="scanned-title">Envíos Escaneados</h2>

        <button className="upload-button" onClick={() => setShowModal(true)}>
          Subir Archivo
        </button>
      </div>

      {/* Barra de filtros */}
      <div className="filters-bar">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar por nombre de archivo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="week-filter">
          <label className="week-toggle">
            <input
              type="checkbox"
              checked={useWeekFilter}
              onChange={(e) => setUseWeekFilter(e.target.checked)}
            />
            Filtrar por semana
          </label>

          {useWeekFilter && (
            <>
              <div className="week-controls">
                <button onClick={goPrevWeek} className="week-nav-btn">⟨ Semana anterior</button>
                <button onClick={goThisWeek} className="week-nav-btn">Esta semana</button>
                <button onClick={goNextWeek} className="week-nav-btn">Semana siguiente ⟩</button>
              </div>

              <div className="week-summary">
                <div className="week-label">
                  <strong>Semana {weekNumber}</strong> de {year}
                </div>
                <div className="week-range">
                  {formatRange(selectedWeekStart, selectedWeekEnd)}
                </div>
              </div>
              <div className="week-jump">
                <label>Semana:&nbsp;</label>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={inputWeek}
                  onChange={(e) => setWeekInputs((s) => ({ ...s, inputWeek: e.target.value }))}
                  onKeyDown={handleWeekKey}
                  style={{ width: 80 }}
                />
                &nbsp;&nbsp;
                <label>Año:&nbsp;</label>
                <input
                  type="number"
                  value={inputYear}
                  onChange={(e) => setWeekInputs((s) => ({ ...s, inputYear: e.target.value }))}
                  onKeyDown={handleWeekKey}
                  style={{ width: 100 }}
                />
                &nbsp;&nbsp;
                <button className="week-nav-btn" onClick={applyWeekJump}>Ir</button>
              </div>

            </>
          )}
        </div>
      </div>

      {loading ? (
        <p>Cargando archivos...</p>
      ) : filteredFiles.length === 0 ? (
        <p>No hay archivos disponibles.</p>
      ) : (
        <div className="file-list">
          {filteredFiles.map((file) => (
            <div className="file-card" key={file.fileId}>
              <h4>{file.originalName}</h4>
              <p><strong>Tamaño:</strong> {formatSize(file.size)}</p>
              <p><strong>Fecha:</strong> {new Date(file.creationDate).toLocaleDateString()}</p>
              <div className="file-card-buttons">
                <button className="file-link" onClick={() => handleViewFile(file.s3Name)}>
                  Ver archivo
                </button>
                <button className="delete-button" onClick={() => handleDeleteFile(file.fileId)}>
                  Borrar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Subir Archivo</h3>
            <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} />
            <div className="modal-buttons">
              <button onClick={() => setShowModal(false)}>Cancelar</button>
              <button onClick={handleFileUpload}>Subir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnviosEscaneados;
